import { CLUSTER } from '../constants';
import { isEnhancedWebviewFeatureSupported } from '@buzzfeed/bf-utils/lib/webview';
import { getXsrfToken } from './xsrf';

async function fetchPaginated(url, { maxResults = Infinity } = {}) {
  let results = [];
  let nextUrl = url;
  while (nextUrl) {
    // Strip domain from next URL in dev environment
    let nextUrlResult = nextUrl;
    if (CLUSTER === 'dev' && nextUrlResult) {
      try {
        const urlObj = new URL(nextUrlResult, window.location.origin);
        nextUrlResult = urlObj.pathname + urlObj.search;
      } catch (e) {
        // If URL parsing fails, assume it's already a path and use as-is
        console.warn('Failed to parse next URL:', e);
      }
    }
    const response = await fetch(nextUrlResult);

    if (!response.ok) {
      throw new Error(`Failed to fetch paginated results: ${response.status}`);
    }

    const data = await response.json();

    if (data.items?.length) {
      results = results.concat(data.items);
    }

    // Stop fetching if we've reached the maximum number of results
    /** TODO: add metrics (JIRA ticket IS-3578) to track if this is a common
     * occurrence and adjust page size or maxResults as needed
     * */
    if (results.length >= maxResults) {
      results = results.slice(0, maxResults);
      break;
    }

    nextUrl = data.next;
  }
  return results;
}

export const getUserFollowing = async (userId, { maxResults } = {}) => {
  try {
    const sanitizedUserId = userId.replace(/island-/, '');
    const results = await fetchPaginated(
      `/island-api/v1/users/${sanitizedUserId}/following?page_size=100`,
      { maxResults }
    );
    return results;
  } catch (e) {
    console.error(`Failed to fetch user following: ${e.message}`);
    return { error: e.message || 'Failed to fetch user following' };
  }
};

export const getUserFollowers = async (userId, { maxResults } = {}) => {
  try {
    const sanitizedUserId = userId.replace(/island-/, '');
    const results = await fetchPaginated(
      `/island-api/v1/users/${sanitizedUserId}/followers?page_size=100`,
      { maxResults }
    );
    return results;
  } catch (e) {
    console.error(`Failed to fetch user followers: ${e.message}`);
    return { error: e.message || 'Failed to fetch user followers' };
  }
};

export const searchUsers = async (query, nextUrl = null, signal = null) => {
  try {
    if (!query || query.trim().length === 0) {
      return { items: [], next: null };
    }

    // Use the full next URL if provided, otherwise build initial URL
    const url = nextUrl || `/island-api/v1/search/users?query=${encodeURIComponent(query)}&page_size=20`;

    const response = await fetch(url, signal ? { signal } : undefined);
    if (!response.ok) {
      throw new Error(`Failed to search users: ${response.status}`);
    }
    const data = await response.json();

    // Strip domain from next URL in dev environment
    let nextUrlResult = data.next || null;
    if (CLUSTER === 'dev' && nextUrlResult) {
      try {
        const urlObj = new URL(nextUrlResult, window.location.origin);
        nextUrlResult = urlObj.pathname + urlObj.search;
      } catch (e) {
        // If URL parsing fails, assume it's already a path and use as-is
        console.warn('Failed to parse next URL:', e);
      }
    }

    return {
      items: data.items || [],
      next: nextUrlResult,
    };
  } catch (e) {
    if (e.name === 'AbortError') {
      // Request was aborted - this is expected behavior
      return { items: [], next: null };
    }
    console.error(`Failed to search users: ${e.message}`);
    return { error: e.message || 'Failed to search users' };
  }
};

export const followUser = async (userId) => {
  try {
    const response = await fetch('/island-api/v1/users/follow', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-xsrftoken': getXsrfToken(),
      },
      body: JSON.stringify({ user_id: userId }),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to follow user: ${response.status}`);
    }

    return await response.json();
  } catch (e) {
    return { error: e.message || 'Failed to follow user' };
  }
};

export const unfollowUser = async (userId) => {
  try {
    const response = await fetch(`/island-api/v1/users/follow?user_id=${userId}`, {
      method: 'DELETE',
      headers: {
        'X-xsrftoken': getXsrfToken(),
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to unfollow user: ${response.status}`);
    }

    return { success: true };
  } catch (e) {
    return { error: e.message || 'Failed to unfollow user' };
  }
};

export const canUserInviteFriends = async () => {
  try {
    // make sure the webview feature is supported
    const canNavigateToInvite = isEnhancedWebviewFeatureSupported({
      feature: 'navigate',
      params: {
        path: '/invite',
      },
      version: 1,
    });

    if (!canNavigateToInvite) {
      return false;
    }

    return true;
  } catch (e) {
    console.error(`Failed to determine invite eligibility: ${e.message}`);
    return false;
  }
};
